/**
 * Aperçu d'un widget hors grille (catalogue, choix de taille, configurateur).
 * Désactive les actions d'édition via WidgetPreviewContext.
 */
import { ComponentType, useLayoutEffect, useRef, useState } from "react";
import { WidgetTypeId } from "./widgetCatalog";
import { AlarmsWidget } from "./widgets/AlarmsWidget";
import { RegulatorWidget } from "./widgets/RegulatorWidget";
import { ChartWidget } from "./widgets/ChartWidget";
import { ContactsWidget } from "./widgets/ContactsWidget";
import { NewsWidget } from "./widgets/NewsWidget";
import { TextWidget } from "./widgets/TextWidget";
import { WeatherWidget } from "./widgets/WeatherWidget";
import { WidgetPreviewContext } from "./widgets/WidgetShell";

/** Aligné sur dashboardGridOptions (cellHeight: 50, margin: 10). */
const GRID_CELL_HEIGHT = 50;
const GRID_MARGIN = 10;
/** Largeur d'une colonne dans l'aperçu (ratio visuel proche de la grille 12 colonnes). */
const PREVIEW_COLUMN_UNIT = 72;

export function getWidgetPreviewDimensions(w: number, h: number) {
  return {
    width: w * PREVIEW_COLUMN_UNIT + Math.max(0, w - 1) * GRID_MARGIN,
    height: h * GRID_CELL_HEIGHT + Math.max(0, h - 1) * GRID_MARGIN,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const WIDGET_COMPONENTS: Record<WidgetTypeId, ComponentType<any>> = {
  RegulatorWidget,
  ChartWidget,
  WeatherWidget,
  ContactsWidget,
  AlarmsWidget,
  NewsWidget,
  TextWidget,
};

type WidgetPreviewProps = {
  widgetType: WidgetTypeId;
  props?: Record<string, unknown>;
  sizeLabel?: string;
  className?: string;
  /** Dimensions en cellules GridStack (w × h). */
  w?: number;
  h?: number;
  /**
   * Largeur d'aperçu (px) servant de base au calcul d'échelle. À renseigner avec
   * la plus grande largeur d'un groupe d'aperçus pour qu'ils partagent une échelle
   * commune et conservent leurs tailles relatives. Par défaut : la largeur propre.
   */
  referenceWidth?: number;
};

export function WidgetPreview({
  widgetType,
  props = {},
  sizeLabel,
  className = "",
  w,
  h,
  referenceWidth,
}: WidgetPreviewProps) {
  const Component = WIDGET_COMPONENTS[widgetType];
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  const dimensions =
    w !== undefined && h !== undefined
      ? getWidgetPreviewDimensions(w, h)
      : { width: 288, height: 320 };

  // Base d'échelle : la largeur de référence du groupe si fournie, sinon la
  // largeur propre. Une base commune préserve les tailles relatives des aperçus.
  const scaleBasis = referenceWidth ?? dimensions.width;

  // Met l'aperçu à l'échelle de la largeur disponible : les grandes tailles
  // (ex. ChartWidget 8×9 ≈ 646 px) tiennent dans leur colonne sans déborder,
  // tout en conservant exactement le ratio de la grille.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      const available = el.clientWidth;
      setScale(available > 0 ? Math.min(1, available / scaleBasis) : 1);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [scaleBasis]);

  if (!Component) return null;

  return (
    <div
      ref={containerRef}
      className={`flex w-full flex-col items-center gap-3 ${className}`}
    >
      {sizeLabel && (
        <p className="text-sm font-oxaniumMedium text-default-text">
          {sizeLabel}
        </p>
      )}
      {/* Empreinte réduite : occupe dans le flux la place réelle de l'aperçu mis à l'échelle. */}
      <div
        className="shrink-0"
        style={{
          width: dimensions.width * scale,
          height: dimensions.height * scale,
        }}
      >
        <div
          className="rounded-xl overflow-hidden border border-grey-light shadow-md bg-white pointer-events-none origin-top-left"
          style={{
            width: dimensions.width,
            height: dimensions.height,
            transform: `scale(${scale})`,
          }}
        >
          <WidgetPreviewContext.Provider value={true}>
            <Component {...props} />
          </WidgetPreviewContext.Provider>
        </div>
      </div>
    </div>
  );
}

export function renderWidgetPreview(
  widgetType: WidgetTypeId,
  props: Record<string, unknown>
) {
  const Component = WIDGET_COMPONENTS[widgetType];
  return Component ? <Component {...props} /> : null;
}

export { WIDGET_COMPONENTS };
